import { SystemBars, SystemBarsStyle } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { isNative } from './nativeAuth';
import { abonneerOpThema, toegepastThema, type ToegepastThema } from '../thema';

/**
 * Zet de systeembalken in de stand die bij het thema hoort.
 *
 * Capacitor draait de namen om: SystemBarsStyle.Light betekent dónkere tekst, bedoeld
 * voor een lichte ondergrond. De achtergrondkleur zelf is niet instelbaar - de balken
 * liggen over de webview en de strook eronder levert de kleur. In lichte modus is dat de
 * witte kop van CoachLayout, in donkere modus de kaartkleur, dus daar moet de tekst juist
 * licht zijn.
 *
 * SystemBars en niet @capacitor/status-bar: dat laatste stuurt alleen de bovenbalk aan,
 * terwijl Android ook een navigatiebalk onderin heeft die in donkere modus mee moet -
 * anders staan er zwarte icoontjes op een donkere ondergrond. Bovendien werken
 * setBackgroundColor en setOverlaysWebView uit dat plugin niet meer vanaf Android 15,
 * omdat edge-to-edge daar niet meer af te zetten is. De betekenis van Light en Dark is
 * in beide plugins gelijk, dus dit is een een-op-een vervanging.
 *
 * SystemBarsStyle.Default zou het systeem volgen. Bewust niet: de gebruiker mag met
 * "altijd licht" of "altijd donker" over het systeem heen gaan, en dan hoort de balk de
 * app te volgen en niet de telefoon.
 *
 * Wat dit níet bereikt: het opstartscherm en het toetsenbord. Die volgen de systeemtrait
 * van iOS, en die kan alleen met eigen Swift-code (overrideUserInterfaceStyle) worden
 * overruled - Capacitor 8 heeft daar geen API voor. Wie het systeem op licht zet en de app
 * op donker, ziet dus een halve seconde een licht opstartscherm en houdt een licht
 * toetsenbord. Dat is bewust geaccepteerd; zie het plan bij deze wijziging.
 */
export async function pasStatusbalkAan(thema: ToegepastThema): Promise<void> {
  if (!isNative()) return;

  // Een scherm met een donkere kop wil lichte tekst in de balk, welk thema er ook staat.
  const effectief: ToegepastThema = donkereKoppen > 0 ? 'donker' : thema;

  try {
    await SystemBars.setStyle({
      style: effectief === 'donker' ? SystemBarsStyle.Dark : SystemBarsStyle.Light,
    });
  } catch (error) {
    console.error('nativeBootstrap: systeembalken niet ingesteld', error);
  }
}

/** Hoeveel schermen met een donkere kop er nu open staan. */
let donkereKoppen = 0;

/**
 * Lichte tekst in de systeembalken zolang een scherm met een donkere kop open staat - het
 * live verslag. Geeft de opruimfunctie terug die de balk weer aan het thema teruggeeft.
 *
 * Via een teller in deze module en niet door zelf SystemBars aan te roepen: het
 * thema-abonnement uit initNativeShell zet de balk bij elke themawissel opnieuw, en zou een
 * systeemwissel midden in een wedstrijd de tekst anders weer donker maken.
 */
export function forceerDonkereKop(): () => void {
  donkereKoppen += 1;
  void pasStatusbalkAan(toegepastThema());

  let opgeruimd = false;
  return () => {
    if (opgeruimd) return;
    opgeruimd = true;
    donkereKoppen = Math.max(0, donkereKoppen - 1);
    void pasStatusbalkAan(toegepastThema());
  };
}

/**
 * Native afwerking die pas kan zodra de webview draait.
 *
 * Op het web doet dit niets, zodat index.tsx dit onvoorwaardelijk kan aanroepen.
 */
export async function initNativeShell(): Promise<void> {
  if (!isNative()) return;

  // Blijft luisteren: zet de gebruiker de telefoon midden in een sessie op donker, dan
  // moet de balk in de stand 'systeem' meteen mee. Eenmalig zetten bij het opstarten
  // dekt dat niet.
  abonneerOpThema(() => {
    void pasStatusbalkAan(toegepastThema());
  });
  await pasStatusbalkAan(toegepastThema());

  try {
    // De splash blijft anders staan tot de plugin-timeout, ook als de app al klaar is.
    await SplashScreen.hide();
  } catch (error) {
    console.error('nativeBootstrap: splash niet verborgen', error);
  }
}
